import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import TextInput from 'react-autocomplete-input';
import { useStores, useFocus } from 'hooks';
import {
  isEnterKey,
  getTrimmedString,
} from 'components/project/ClerkScreen/shared/utils/common';
import { Icon, Select } from 'components/project/ClerkScreen';
import {
  Container,
  InputButtonGroup,
  SubmitButton,
  Tab,
  TabList,
  TabPanel,
  TabPanelDescriptionContainer,
  TabPanelDescriptionContainer2,
  TabPanelEmphasisLabel,
  TabPanelInput,
  Tabs,
} from './Styles';

const MessageTabs = ({
  replyToId,
  replyToName,
  replyToMemberName,
  itemNumber,
  handleSendMessage,
  handleSendLaneMessage,
  handleSendPinMessage,
  handleClearReplyTo,
  indvMessageRef,
  setIndvMessageElFocus,
  laneMessageRef,
  setLaneMessageElFocus,
  pinMessageRef,
  setPinMessageElFocus,
}) => {
  const {
    AuctionStore: { laneClientList },
    MessageStore: { messaging, handleSetDirectMessageUser },
    BidStore: { auction },
    WebSocketStore: {
      sendLaneMessageToUsersMessage,
      sendPinMessageToUsersMessage,
      sendDirectMessageToUserMessage,
    },
  } = useStores();

  const pinMessageTemplate = [
    { text: 'Choose Template', value: '' },
    {
      text: 'Audio Issue',
      value:
        'We are currently experiencing audio issues and looking into it. Sorry for the inconvenience!',
    },
    {
      text: 'Switching Cart',
      value:
        'We are switching to a different cart and will be back online momentarily. Thank you for your patience!',
    },
  ];

  const { disableGlobalTab, disablePinnedTab, disableUserTab } = messaging;

  const laneBidderInputDefaultValue = replyToName
    ? `${replyToName} (${replyToId})`
    : null;

  const [pinTemplateValue, setPinTemplateValue] = useState(null);
  const [uiMessageSent, setUiMessageSent] = useState(false);
  const [message, setMessage] = useState(null);
  const [laneMessage, setLaneMessage] = useState(null);
  const [pinMessage, setPinMessage] = useState(null);
  const [laneBidderInputValue, setLaneBidderInputValue] = useState(
    laneBidderInputDefaultValue,
  );
  const MAX_PINNED_MESSAGES_CHARS = 160;
  const [pinMessageCharsRemaining, setPinMessageCharsRemaining] = useState(
    MAX_PINNED_MESSAGES_CHARS,
  );
  const [laneBidderRef] = useFocus();

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
  };

  const handleLaneMessageChange = (event) => {
    setLaneMessage(event.target.value);
  };

  const onSendLaneMessage = (msg) => {
    laneMessageRef.current.value = '';
    handleSendLaneMessage(msg);
    setLaneMessage(null);
  };

  const onSendMessage = (msg) => {
    indvMessageRef.current.value = '';
    setIndvMessageElFocus();
    handleSendMessage(msg);
    setMessage(null);
  };

  const handlePinMessageChange = (event) => {
    setPinMessage(event.target.value);
  };

  const onSendPinMessage = (msg) => {
    pinMessageRef.current.value = '';
    handleSendPinMessage(msg);
    setPinMessageCharsRemaining(MAX_PINNED_MESSAGES_CHARS);
    setPinMessage(null);
    setPinTemplateValue(null);
  };

  const listContainsValue = (value) => {
    const obj = laneClientList.filter((item) => {
      if (item.memberName.indexOf('(') > -1)
        return item.memberName.indexOf(value) > -1;
      return item.memberName === value;
    });

    if (obj.length === 0) {
      return [];
    }

    return obj;
  };

  const handleChange = (value) => {
    if (value !== laneBidderRef.current.value) {
      laneBidderRef.current.value = '';
    }

    const laneClient = laneClientList.filter(
      (item) => item.memberName === value,
    );

    if (laneClient.length) {
      setLaneBidderInputValue(value);
      handleClearReplyTo();
      const { bidderNumber, account, member } = laneClient[0];

      // is bidder
      if (bidderNumber !== '')
        handleSetDirectMessageUser(
          member,
          bidderNumber,
          auction.lotNumber,
          value,
        );

      // is seller
      if (bidderNumber === '')
        handleSetDirectMessageUser(member, account, auction.lotNumber, value);
    }
  };

  const handleKeyUp = (e) => {
    const hasValue = listContainsValue(e.currentTarget.value);
    if (!hasValue || e.which === 8) {
      handleSetDirectMessageUser(null, null, null, null);
      setLaneBidderInputValue(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.which === 8) {
      handleSetDirectMessageUser(null, null, null, null);
      setLaneBidderInputValue(null);
    }
  };

  const handleIndividualMessageKeyPress = (e) => {
    if (!isEnterKey(e));

    if (isEnterKey(e) && getTrimmedString(message).length > 0) {
      e.preventDefault();
      onSendMessage(message);
    }
  };

  const handleGlobalMessageKeyPress = (e) => {
    if (isEnterKey(e) && getTrimmedString(laneMessage).length > 0) {
      e.preventDefault();
      onSendLaneMessage(laneMessage);
    }
  };

  const handlePinMessageKeyPress = (e) => {
    if (isEnterKey(e) && getTrimmedString(pinMessage).length > 0) {
      e.preventDefault();
      onSendPinMessage(pinMessage);
    }
  };

  const handleMessageTabClick = (tabIndex) => {
    if (tabIndex === 0) {
      sendDirectMessageToUserMessage();
      setIndvMessageElFocus();
      return;
    }

    if (tabIndex === 1) {
      sendLaneMessageToUsersMessage();
      setLaneMessageElFocus();
      return;
    }

    if (tabIndex === 2) {
      sendPinMessageToUsersMessage();
      setPinMessageElFocus();
    }
  };

  useEffect(() => {
    setPinMessageCharsRemaining(
      pinMessage
        ? MAX_PINNED_MESSAGES_CHARS - pinMessage.length
        : MAX_PINNED_MESSAGES_CHARS,
    );
    sendPinMessageToUsersMessage();
  }, [pinMessage]);

  useEffect(() => {
    if (itemNumber) {
      if (itemNumber !== 'No item') {
        setLaneBidderInputValue(`${replyToMemberName} - Item #${itemNumber}`);
      } else {
        setLaneBidderInputValue(`${replyToMemberName}`);
      }
      sendDirectMessageToUserMessage();
      setIndvMessageElFocus();
    }
    // eslint-disable-next-line
  }, [replyToId, replyToName, replyToMemberName, itemNumber]);

  useEffect(() => {
    // if (!uiMessageSent && indvMessageRef?.current?.value !== '') {
    if (indvMessageRef?.current?.value !== '') {
      if (message?.length) {
        // setUiMessageSent(true);
        sendDirectMessageToUserMessage();
        setIndvMessageElFocus();
      }
      if (laneMessage?.length) {
        // setUiMessageSent(true);
        sendLaneMessageToUsersMessage();
        setLaneMessageElFocus();
      }
      if (pinMessage?.length) {
        // setUiMessageSent(true);
        sendPinMessageToUsersMessage();
        setPinMessageElFocus();
      }
    }
  }, [message, laneMessage, pinMessage, indvMessageRef?.current?.value]);

  return (
    <Container>
      <Tabs onSelect={(tabIndex) => handleMessageTabClick(tabIndex)}>
        <TabList>
          <Tab disabled={disableUserTab}>User</Tab>
          <Tab disabled={disableGlobalTab}>Global</Tab>
          <Tab disabled={disablePinnedTab}>Pinned</Tab>
        </TabList>
        <TabPanel>
          <TabPanelDescriptionContainer>
            <InputButtonGroup>
              <TextInput
                data-testid="chat-autocomplete"
                Component="input"
                spacer=""
                matchAny
                minChars={1}
                maxOptions={10}
                trigger=""
                regex="^[A-Za-z0-9\s\\-]+$"
                offsetY={5}
                offsetX={0}
                options={laneClientList.map((item) => item.memberName)}
                placeholder="Start typing to select member"
                autoComplete="off"
                defaultValue={laneBidderInputValue}
                disabled={false}
                ref={laneBidderRef}
                value={laneBidderInputValue}
                onFocus={(e) => e.currentTarget.select()}
                onKeyUp={handleKeyUp}
                onKeyPress={handleKeyPress}
                onChange={(value) => handleChange(value)}
              />
            </InputButtonGroup>
          </TabPanelDescriptionContainer>
          <TabPanelInput
            ref={indvMessageRef}
            onChange={handleMessageChange}
            onKeyPress={handleIndividualMessageKeyPress}
            disabled={!replyToMemberName}
            placeholder={
              replyToMemberName ? 'Type message...' : 'Select a user...'
            }
          />
          <SubmitButton
            bgColor="#04A1D3"
            disabled={getTrimmedString(message).length === 0}
            onClick={() => onSendMessage(message)}
          >
            <Icon icon="paper-plane-o" size={1} />
            Send to Member
          </SubmitButton>
        </TabPanel>

        <TabPanel>
          <TabPanelDescriptionContainer2>
            <TabPanelEmphasisLabel>
              Send message to all enabled users
            </TabPanelEmphasisLabel>
          </TabPanelDescriptionContainer2>
          <TabPanelInput
            ref={laneMessageRef}
            onChange={handleLaneMessageChange}
            placeholder="Type message..."
            onKeyPress={handleGlobalMessageKeyPress}
          />
          <SubmitButton
            bgColor="#1C6C98"
            disabled={getTrimmedString(laneMessage).length === 0}
            onClick={() => onSendLaneMessage(laneMessage)}
          >
            <Icon icon="paper-plane-o" size={1} />
            Send to All
          </SubmitButton>
        </TabPanel>
        <TabPanel>
          <TabPanelDescriptionContainer2>
            <TabPanelEmphasisLabel>
              Pin a message to the bid client (Chars remaining:
              {pinMessageCharsRemaining})
            </TabPanelEmphasisLabel>
            {/* <ChooseTemplateLink href="#">
              Choose from template
            </ChooseTemplateLink> */}
          </TabPanelDescriptionContainer2>
          <TabPanelInput
            ref={pinMessageRef}
            onChange={handlePinMessageChange}
            onKeyPress={handlePinMessageKeyPress}
            maxLength={MAX_PINNED_MESSAGES_CHARS}
            placeholder="Type message..."
            value={pinMessage}
          />
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ width: '60%', paddingTop: '2px', fontSize: '.75rem' }}>
              <Select
                valueProp="value"
                textProp="text"
                value={pinTemplateValue}
                currentValue={pinTemplateValue}
                data={pinMessageTemplate}
                noneSelectedText=""
                onChange={(e) => {
                  setPinTemplateValue(e.target.value);
                  handlePinMessageChange(e);
                }}
              />
            </div>
            <div>
              <SubmitButton
                bgColor="#F57A3A"
                disabled={getTrimmedString(pinMessage).length === 0}
                onClick={() => onSendPinMessage(pinMessage)}
              >
                <Icon icon="thumb-tack" size={1} />
                Pin Message
              </SubmitButton>
            </div>
          </div>
        </TabPanel>
      </Tabs>
    </Container>
  );
};

MessageTabs.propTypes = {
  replyToId: PropTypes.string,
  replyToName: PropTypes.string,
  replyToMemberName: PropTypes.string,
  itemNumber: PropTypes.string,
  handleSendMessage: PropTypes.func.isRequired,
  handleSendLaneMessage: PropTypes.func.isRequired,
  handleSendPinMessage: PropTypes.func.isRequired,
  handleClearReplyTo: PropTypes.func.isRequired,
  indvMessageRef: PropTypes.object.isRequired,
  setIndvMessageElFocus: PropTypes.func.isRequired,
  laneMessageRef: PropTypes.object.isRequired,
  setLaneMessageElFocus: PropTypes.func.isRequired,
  pinMessageRef: PropTypes.object.isRequired,
  setPinMessageElFocus: PropTypes.func.isRequired,
};
MessageTabs.defaultProps = {
  replyToId: null,
  replyToName: null,
  replyToMemberName: null,
  itemNumber: null,
};

export default observer(MessageTabs);
